import { useEffect, useState } from 'react';
import { ExternalLink, X } from 'lucide-react';
import { api } from '../api/client';
import { useT } from '../i18n';
import type { Project } from '../types';
import { projectDisplayCode } from '../utils/identifiers';
import { projectToForm, VALUE_ADDED_OPTIONS, EFFORT_OPTIONS, TIMELINE_OPTIONS, type FormState } from './ProjectForm';
import { DeliverableAccordionList } from './DeliverablesEditor';

// ── Project overview, as an overlay ──
// A read-only copy of the project page's "Aperçu" tab, opened from a task card
// (My calendar) so the person can see what the project is about without leaving
// the page. Same sections, same labels, same parser as the project page; the
// footer opens the full project page.

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      {children}
    </div>
  );
}

function Text({ value }: { value: string }) {
  return <div className="form-input--bare" style={{ cursor: 'default', whiteSpace: 'pre-wrap' }}>{value || '—'}</div>;
}

// The option chips of the project page, with only the chosen ones lit.
function Chips({ options, chosen }: { options: string[]; chosen: string[] }) {
  return (
    <div className="selectable-group">
      {options.map((opt) => (
        <button key={opt} type="button" className={`selectable${chosen.includes(opt) ? ' active' : ''}`} disabled>{opt}</button>
      ))}
    </div>
  );
}

function Overview({ project, form }: { project: Project; form: FormState }) {
  const { t } = useT();
  // Team: the scoping allocations once they exist, else the collaborators named at intake.
  const roster = form.allocations.length > 0
    ? form.allocations.map((a) => ({ name: a.name, pct: a.allocationPct }))
    : form.collaborators.map((c) => ({ name: c.name, pct: '' }));
  const labels = [project.status_label, project.domain_label, project.level_label].filter(Boolean) as string[];

  return (
    <>
      <div className="po-head">
        <div className="po-labels">
          {labels.map((l) => <span key={l} className="po-label">{l}</span>)}
          {project.is_blocked && <span className="po-label po-label--blocked">{t('projectDetail.blockedBadge')}</span>}
        </div>
        {project.is_blocked && project.blocked_reason && <Text value={project.blocked_reason} />}
      </div>

      <div className="form-section">
        <div className="section-label mb-12">{t('projectDetail.overview.problemSectionLabel')}</div>
        <Field label={t('projectDetail.overview.problemLabel')}><Text value={form.problem} /></Field>
        <Field label={t('projectDetail.overview.goalLabel')}><Text value={form.goal} /></Field>
        <Field label={t('projectDetail.overview.valueAddedLabel')}><Chips options={VALUE_ADDED_OPTIONS} chosen={form.valueAdded} /></Field>
      </div>

      <div className="form-section">
        <div className="section-label mb-12">{t('projectDetail.overview.deliverableSectionLabel')}</div>
        <Field label={t('projectDetail.overview.deliverableFieldLabel')}><DeliverableAccordionList deliverables={form.deliverables} /></Field>
      </div>

      <div className="form-section">
        <div className="section-label mb-12">{t('projectDetail.overview.resourcesSectionLabel')}</div>
        <div className="form-grid-2">
          <Field label={t('projectDetail.overview.effortSizeLabel')}><Chips options={EFFORT_OPTIONS} chosen={[form.effortSize]} /></Field>
          <Field label={t('projectDetail.overview.timelineLabel')}><Chips options={TIMELINE_OPTIONS} chosen={[form.timeline]} /></Field>
        </div>
        <Field label={t('projectDetail.overview.cashBudgetLabel')}><Text value={form.cash} /></Field>
        <Field label={t('projectDetail.overview.collaboratorsLabel')}>
          {roster.length > 0 ? (
            <div className="detail-collabs">
              {roster.map((r, i) => (
                <div key={i} className="detail-collab">
                  <span>{r.name}</span>
                  <span className="alloc-num">{r.pct ? `${r.pct}%` : '—'}</span>
                </div>
              ))}
            </div>
          ) : <Text value="" />}
        </Field>
      </div>

      <div className="form-section">
        <div className="section-label mb-12">{t('projectDetail.overview.ownershipSectionLabel')}</div>
        <div className="form-grid-2">
          <Field label={t('projectDetail.overview.sponsorLabel')}><Text value={form.sponsor} /></Field>
          <Field label={t('projectDetail.overview.pmoAccompanistLabel')}><Text value={form.pmoAccompanist} /></Field>
        </div>
        <Field label={t('projectDetail.overview.userGroupsLabel')}><Text value={form.userGroups.join(', ')} /></Field>
      </div>
    </>
  );
}

export function ProjectOverviewModal({ projectId, onClose }: { projectId: number; onClose: () => void }) {
  const { t } = useT();
  const [project, setProject] = useState<Project | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setProject(null);
    setFailed(false);
    api.getProject(projectId)
      .then((p) => { if (!cancelled) setProject(p as Project); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [projectId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const openPage = () => { onClose(); window.location.hash = `project?id=${projectId}`; };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-wide project-overview-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={t('calendar.projectOverview')}>
        <div className="modal-header">
          <div className="po-title">
            <span className="po-kicker">{t('calendar.projectOverview')}</span>
            <h3 className="modal-title">
              {project && <span className="po-code">{projectDisplayCode(project)}</span>}
              {project ? project.name : failed ? '' : t('common.loading')}
            </h3>
          </div>
          <button type="button" className="close-btn" onClick={onClose} title={t('common.close')} aria-label={t('common.close')}><X size={12} /></button>
        </div>
        <div className="modal-body">
          {failed
            ? <div className="po-failed">{t('calendar.overviewLoadFailed')}</div>
            : project
              ? <Overview project={project} form={projectToForm(project)} />
              : <div className="spinner" />}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>{t('common.close')}</button>
          <button type="button" className="btn btn-accent btn-sm" onClick={openPage} disabled={failed}>
            <ExternalLink size={13} /> {t('calendar.openProjectPage')}
          </button>
        </div>
      </div>
    </div>
  );
}
